import { Grid, TextField } from '@material-ui/core';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import useInput from 'lib/hooks/useInput';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import T from 'components/common/text/T';
import CustomSpan from 'components/common/text/CustomSpan';
import { SerialActions, AppActions } from 'store/actionCreators';
import IconButton from '@material-ui/core/IconButton';
import { useDidUpdateEffect } from 'lib/utils';
import { useShallowAppSelector } from 'store/hooks';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularLoading from 'components/base/loading/CircularLoading';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { useTranslation } from 'react-i18next';
import TrashIcon from 'components/base/icons/TrashIcon';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { machineList } from 'lib/mapper';
import { PageSubTitle } from 'components/base/title/PageTitle';
import { validInputString } from 'lib/library';
import ListEmpty from 'components/common/list/ListEmpty';

interface AccountInfoProps {}

interface IProductSerial {
  groupAlias: string;
  modelGroupAlias: string;
  serialEnrollIdx: number;
  serialNo: string;
  serialCodeSystemIdx: number;
  productGroupName: string;
  productModelName: string;
  enrollDate: number;
}

function WarrantyContainer({}: AccountInfoProps) {
  const {
    fetchProductSerialList: { list: mySerialList, pagingData: serialListPagingData },
    fetchProductSerialListSuccess,
    fetchProductSerialListPending,
    fetchProductSerialListFailure,
    updateProductSerialError,
    updateProductSerialSuccess,
    updateProductSerialFailure,
    deleteProductSerialSuccess,
  }: {
    fetchProductSerialList: { list: IProductSerial[]; pagingData: OffsetPagingData };
    fetchProductSerialListSuccess: boolean;
    fetchProductSerialListPending: boolean;
    fetchProductSerialListFailure: boolean;
    updateProductSerialError: any;
    updateProductSerialSuccess: boolean;
    updateProductSerialFailure: boolean;
    deleteProductSerialSuccess: boolean;
  } = useShallowAppSelector(state => ({
    fetchProductSerialList: state.serial.productSerialList.data || { list: {}, pagingData: {} },
    fetchProductSerialListSuccess: state.serial.productSerialList.success,
    fetchProductSerialListPending: state.serial.productSerialList.pending,
    fetchProductSerialListFailure: state.serial.productSerialList.failure,
    updateProductSerialError: state.serial.updateSerial.error || {},
    updateProductSerialSuccess: state.serial.updateSerial.success,
    updateProductSerialFailure: state.serial.updateSerial.failure,
    deleteProductSerialSuccess: state.serial.deleteSerial.success,
  }));

  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const { t } = useTranslation();
  const hasMoreSerialList = serialListPagingData?.page < serialListPagingData?.totalPage;
  const page = useInput(1);
  const [mySerialListStack, setMySerialListStack] = useState<IProductSerial[]>([]);

  const searchSerialNumber = useInput('');
  const searchSerialError = useInput('');
  const deleteSerialNumber = useInput('');

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedProductObj, setSelectedProductObj] = useState<IProductSerial | null>(null);

  const handleFetchMySerialList = () => {
    page.setValue((draft: number) => draft + 1);
    const submitData = {
      page: page.value + 1,
    };
    SerialActions.fetch_product_serial_list_request(submitData);
  };

  const handleClickAddProduct = useCallback(() => {
    let checkValid = validInputString(searchSerialNumber.value);
    searchSerialNumber.setValue(checkValid);
    SerialActions.update_product_serial_request({ serialNo: checkValid });
  }, [searchSerialNumber.value]);

  const handleRefreshList = () => {
    SerialActions.fetch_product_serial_list_request({ page: 1 });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useEffect(() => {
    SerialActions.fetch_product_serial_list_request({ page: 1 });
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  useDidUpdateEffect(() => {
    if (deleteProductSerialSuccess && !!deleteSerialNumber.value) {
      // 시리얼 삭제 성공 시, 현재 불러온 시리얼 목록에서 해당 시리얼 삭제
      const deleteAfterSerial: any[] = mySerialListStack.filter(item => {
        return item.serialNo !== deleteSerialNumber.value;
      });
      setMySerialListStack([...deleteAfterSerial]);
      deleteSerialNumber.setValue('');
    }
  }, [deleteProductSerialSuccess, deleteSerialNumber.value]);

  useDidUpdateEffect(() => {
    if (updateProductSerialSuccess) {
      // 시리얼 등록 성공 시, 시리얼 리스트 초기화
      setIsLoadFailure(false);
      setIsLoadSuccess(false);
      SerialActions.fetch_product_serial_list_request({ page: 1 });
      page.setValue(1);
      searchSerialNumber.setValue('');
    }
  }, [updateProductSerialSuccess]);

  useDidUpdateEffect(() => {
    if (updateProductSerialFailure) {
      // 시리얼 등록 실패 시
      searchSerialError.setValue(`SERIAL_${updateProductSerialError.message}`);
    }
  }, [updateProductSerialFailure]);

  useDidUpdateEffect(() => {
    if (!fetchProductSerialListSuccess) return;
    if (page.value === 1) {
      setMySerialListStack(mySerialList);
      setIsLoadSuccess(true);
      if (!selectedProductObj && mySerialList.length > 0) {
        //1페이지 로드 후, 선택 되어있는 시리얼이 없다면 리스트의 0번항목 자동 선택
        setSelectedProduct(mySerialList[0].serialNo);
        setSelectedProductObj(mySerialList[0]);
      }
    } else {
      setMySerialListStack(draft => [...draft, ...mySerialList]);
    }
  }, [!!fetchProductSerialListSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchProductSerialListFailure) return;
    setIsLoadFailure(true);
  }, [!!fetchProductSerialListFailure]);

  const noticeList = {
    title: t('GLOBAL_CONFIRM_DELETION'),
    mainText: t('GLOBAL_DELETE_DATA'),
    subText: '',
  };

  const alertDeleteSerial = (serialNumber: string, isActive: boolean) => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={noticeList.title}
          mainText={noticeList.mainText}
          subText={noticeList.subText}
        />
      ),
      onClick() {
        if (isActive) {
          setSelectedProduct('');
          setSelectedProductObj(null);
        }
        deleteSerialNumber.setValue(serialNumber);
        SerialActions.delete_product_serial_request({ serialNo: serialNumber });
      },
      onCancel() {
        return;
      },
    });
  };

  return (
    <StyledAcctounInfo data-component-name="AcctounInfo">
      <div className="acctounInfo__content_wrapper warranty">
        <PageSubTitle title={t('TITLE_WARRANTY')} />

        <Grid container className="acctounInfo__content_warranty_box products">
          <Grid item className="acctounInfo__warranty_products_image_box">
            {useMemo(() => {
              if (!selectedProductObj && !isLoadSuccess) return;
              if (!selectedProductObj && !!isLoadSuccess && mySerialListStack.length > 0)
                return (
                  <span>
                    <T>DEVICE_SELECT</T>
                  </span>
                );

              const findImage = machineList.find(
                machine => machine.modelGroupAlias === selectedProductObj?.modelGroupAlias,
              )?.image;
              return <img className="warranty_products_image" src={findImage} />;
            }, [selectedProductObj, isLoadSuccess])}
          </Grid>
          <Grid item className="acctounInfo__content_warranty_table_wrapper products">
            <div className="acctounInfo__content_warranty_search products">
              <div className="acctounInfo__content_warranty_search_bar_box">
                <div className="acctounInfo__content_warranty_search_label">
                  <CustomSpan fontSize={18} fontWeight={500}>
                    <T>SERIAL_NUMBER</T>
                  </CustomSpan>
                </div>
                <div className="acctounInfo__content_warranty_search_bar">
                  {useMemo(
                    () => (
                      <MuiWrapper>
                        <TextField
                          multiline
                          rows={1}
                          className="product_search_bar"
                          name="serialNumber"
                          type="text"
                          placeholder={t('SERIAL_NUMBER_PLACEHOLDER')}
                          inputProps={{
                            maxLength: 32,
                          }}
                          variant="outlined"
                          fullWidth
                          autoComplete="off"
                          value={searchSerialNumber.value}
                          onChange={e => {
                            searchSerialNumber.onChange(e);
                            searchSerialError.setValue('');
                          }}
                        />
                      </MuiWrapper>
                    ),
                    [searchSerialNumber.value],
                  )}
                  <div className="serial_error">{t(searchSerialError.value)}</div>
                </div>
              </div>
              <div className="acctounInfo__content_warranty_search_btn_box">
                <MuiButton
                  disableElevation
                  className="prduct_add_button"
                  variant="contained"
                  onClick={handleClickAddProduct}
                >
                  <CustomSpan fontSize={13} fontWeight={400}>
                    {'+ '}
                    <T>ADD_PRODUCT</T>
                  </CustomSpan>
                </MuiButton>
              </div>
            </div>
            <div className="acctounInfo__content_warranty_table products">
              <div className="acctounInfo_warranty_table_head">
                <Grid container className="acctounInfo_warranty_table_row">
                  <Grid item className="warranty_table_row_item">
                    <T>PRODUCT_NAME</T>
                  </Grid>
                  <Grid item className="warranty_table_row_item">
                    <T>SERIAL_NUMBER</T>
                  </Grid>
                  <Grid item className="warranty_table_row_item"></Grid>
                  <Grid item className="warranty_table_row_item"></Grid>
                  <Grid item className="warranty_table_row_item">
                    {' '}
                  </Grid>
                </Grid>
              </div>
              <div className="acctounInfo_warranty_table_body_wrapper">
                {mySerialListStack.length < 1 && fetchProductSerialListPending && (
                  <div className="infinite-scroll-loading">
                    <CircularLoading />
                  </div>
                )}

                {useMemo(
                  () => (
                    <InfiniteScroll
                      className={cx('acctounInfo_warranty_table_body', {
                        isScroll: mySerialListStack.length > 5 ? true : false,
                      })}
                      dataLength={mySerialListStack?.length}
                      next={handleFetchMySerialList}
                      hasMore={hasMoreSerialList}
                      loader={
                        <div className="infinite-scroll-loading">
                          <CircularLoading />
                        </div>
                      }
                      height={254}
                      scrollThreshold={0.95}
                    >
                      {!!isLoadSuccess &&
                        (mySerialListStack.length > 0 ? (
                          mySerialListStack?.map((item: IProductSerial, index: number) => {
                            const isActive = selectedProduct === item.serialNo;
                            return (
                              <Grid
                                container
                                className={cx('acctounInfo_warranty_table_row', {
                                  active: isActive,
                                })}
                                key={index}
                                onClick={() => {
                                  if (isActive) {
                                    setSelectedProduct('');
                                    setSelectedProductObj(null);
                                  } else {
                                    setSelectedProduct(item.serialNo);
                                    setSelectedProductObj(item);
                                  }
                                }}
                              >
                                <Grid item className="warranty_table_row_item">
                                  {item.productModelName}
                                </Grid>
                                <Grid item className="warranty_table_row_item">
                                  {item.serialNo}
                                </Grid>
                                <Grid item className="warranty_table_row_item"></Grid>
                                <Grid item className="warranty_table_row_item"></Grid>
                                <Grid item className="warranty_table_row_item">
                                  <IconButton
                                    className="product_delete_button"
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      alertDeleteSerial(item.serialNo, isActive);
                                    }}
                                  >
                                    <TrashIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            );
                          })
                        ) : (
                          <ListEmpty text="GLOBAL_EMPTY_LIST_DEVICE" />
                        ))}
                    </InfiniteScroll>
                  ),
                  [mySerialListStack, selectedProduct, isLoadSuccess],
                )}
                {!!isLoadFailure && (
                  <ListEmpty
                    text="GLOBAL_LIST_ERROR"
                    isRefreshButton={true}
                    onRefresh={handleRefreshList}
                  />
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </StyledAcctounInfo>
  );
}

const StyledAcctounInfo = styled.section<{}>`
  .acctounInfo__sales_unit_wrapper {
    padding-bottom: 40px;
  }
  .acctounInfo__content_wrapper {
    flex-wrap: wrap;
    &.sales {
      width: 466px;
      margin-bottom: 20px;
    }
    &.unit {
      width: 1162px;
      margin-left: -34px;
      margin-bottom: 20px;
    }
    &.employee {
      margin-bottom: 90px;
    }
    .acctounInfo__content_title {
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      .acctounInfo__content_title_icon {
        width: 60px;
        height: 60px;
      }
    }

    .acctounInfo__content_warranty_box {
      margin-bottom: 60px;

      .acctounInfo__warranty_products_image_box {
        width: 466px;
        padding-right: 16px;
        height: 450px;
        box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.16);
        border-radius: 15px;
        background-color: #fff;
        display: inline-flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        row-gap: 11px;
        .warranty_products_image {
          height: 340px;
        }
      }

      .acctounInfo__content_warranty_table_wrapper {
        background-color: #fff;
        position: relative;
        width: calc(100% - 450px);
        height: 450px;
        margin-left: -16px;
        border-radius: 15px;
        box-shadow: 0px 0px 6px rgb(0 0 0 / 16%);
        background-color: #ffffff;
      }
      .acctounInfo__content_warranty_search {
        width: 100%;
        height: 125px;
        padding: 40px 30px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        .acctounInfo__content_warranty_search_bar_box {
          display: flex;
          align-items: center;
          column-gap: 20px;
          .acctounInfo__content_warranty_search_bar {
            position: relative;
            .product_search_bar {
              width: 605px;
            }
            .serial_error {
              position: absolute;
              font-size: 12px;
              font-weight: 500;
              bottom: -25px;
              left: 0;
              color: #f00;
            }
          }
        }
        .acctounInfo__content_warranty_search_btn_box {
          .prduct_add_button {
            width: 300px;
            height: 40px;
            border-radius: 20px;
            background-color: #00a4e3;
            box-shadow: inset 3px 3px 6px rgb(0 0 0 / 16%);
          }
        }
      }
      .acctounInfo__content_warranty_table {
        padding: 0 30px;
        &.products {
          width: 100%;
          height: 325px;
        }

        .acctounInfo_warranty_table_head {
          height: 40px;
          border-radius: 5px;
          background-color: #edf4fb;
          .acctounInfo_warranty_table_row {
            height: 40px;
            font-size: 15px;
          }
        }
        .acctounInfo_warranty_table_body_wrapper {
          position: relative;
          height: 254px;
          width: 100%;
          .acctounInfo_warranty_table_body {
            height: 254px;
            margin-right: -16px;
            padding-right: 16px;
            &.isScroll {
              padding-right: 0px;
            }
            .acctounInfo_warranty_table_row {
              height: 50px;
              font-size: 16px;
            }
          }
        }
        .acctounInfo_warranty_table_row {
          width: 100%;
          align-items: center;
          border-bottom: 1px solid #edf4fb;
          position: relative;
          &.active {
            font-weight: 500;
          }
          .warranty_table_row_item {
            &:nth-child(1) {
              width: 18%;
              padding-left: 30px;
            }
            &:nth-child(2) {
              width: 27%;
            }
            &:nth-child(3) {
              width: 27%;
            }
            &:nth-child(4) {
              width: 21%;
            }
            &:nth-child(5) {
              width: 7%;
              text-align: center;
            }
          }

          &:hover {
            .product_delete_button {
              cursor: pointer;
            }
            background-color: #ebf8fc;
          }
        }
      }
    }
  }
`;

export default React.memo(WarrantyContainer);
